import { get, pick, cloneDeep } from 'lodash';
import dayjs from 'dayjs';
import request from '@/services/request';
import StripeService from '@/services/stripe';
import CardknoxService from '@/services/cardknox';
import zelle from '@/store/modules/donation/zelle';

import { GATEWAYS, PAYMENT_METHODS, getCurrencySymbol, sanitizeEmail } from '@/utils';
import { currencies } from '@/services/currencies';
import { getSentryScope } from '@/services/sentry';
const DEFAULT_CURRENCY = 'usd';
const PREFILL_FIELDS = ['email', 'firstName', 'lastName'];
// Should be defined on the server, but added here as a fallback
const DEFAULT_FEES = {
  fixedAmount: { cc: 0.3, plaid: 0.3 },
  percentageAmount: { cc: 2.9, plaid: 0.8 }
};

export const state = {
  amount: null,
  amountDescription: null,
  gateway: null, // actual gateway used
  CCGateway: null, // cc gateway based on conditions
  paypalGateway: null,
  ...zelle.state,
  paymentMethod: null, // PAYMENT_METHODS
  achFlow: 'financial_connections',
  cardExpiry: null, // cardknox card
  accountName: null, // cardknox ach
  routingNumber: null, // cardknox ach
  campaignId: null,
  currency: DEFAULT_CURRENCY,
  programmaticCurrency: null, // Currency set via Double.openCheckout({currency: 'XXX'}) - has highest precedence
  orgDefaultCurrency: null,
  entityId: null,
  frequency: null, // 'single' | 'weekly' | 'monthly' | 'yearly'
  Type: null, // 'byCharges', 'byDate'
  startDate: null,
  numCharges: null,
  payCoverFees: false,
  customQuestions: [],
  comment: null,
  anonymous: false,
  fundraiserId: null,
  solicitorId: null,
  solicitorSlug: null,
  designation: null,
  isDedicating: false,
  dedication: {},
  donorDetails: {},
  prefill: {},
  meta: {},
  billingAddress: {},
  stripeIntent: {},
  chariotIntent: {},
  cardknoxTokens: {},
  donationId: null,
  plaidLinkToken: null,
  plaidPublicToken: null,
  plaidData: null,
  hcaptchaToken: null,
  donationDraftId: null,
  utmParameters: {},
  sourceUrl: null,
  upsell: null,
  donationItems: [],
  donationItemsOtherAmount: null
};

export const mutations = {
  ...zelle.mutations,
  SET_DESIGNATION(state, newValue) {
    state.designation = newValue;
  },
  SET_AMOUNT(state, newValue) {
    if (!newValue) {
      return;
    }
    state.upsell = null;
    state.amount = Number(Number(newValue).toFixed(2));
  },
  RESET_AMOUNT(state) {
    state.amount = null;
    state.upsell = null;
  },
  SET_AMOUNT_DESCRIPTION(state, newValue) {
    state.amountDescription = newValue;
  },
  SET_DONATION_ITEM_QUANTITY(state, { item, quantity }) {
    const normalizedQuantity = Math.max(Number(quantity) || 0, 0);
    const existingIndex = state.donationItems.findIndex((selection) => selection.id === item.id);

    if (!normalizedQuantity) {
      if (existingIndex >= 0) {
        state.donationItems.splice(existingIndex, 1);
      }
      return;
    }

    const selection = {
      id: item.id,
      name: item.name,
      imageUrl: item.imageUrl,
      amount: Number(item.amount),
      currency: item.currency,
      quantity: normalizedQuantity
    };

    if (existingIndex >= 0) {
      state.donationItems.splice(existingIndex, 1, selection);
      return;
    }

    state.donationItems.push(selection);
  },
  RESET_DONATION_ITEMS(state) {
    state.donationItems = [];
  },
  SET_DONATION_ITEMS_OTHER_AMOUNT(state, newValue) {
    const amount = Number(newValue);
    state.donationItemsOtherAmount = Number.isFinite(amount) && amount > 0 ? Number(amount.toFixed(2)) : null;
  },
  SET_GATEWAY(state, newValue) {
    state.gateway = newValue;
  },
  SET_CC_GATEWAY(state, newValue) {
    state.CCGateway = newValue;
  },
  SET_PAYPAL_GATEWAY(state, newValue) {
    state.paypalGateway = newValue;
  },
  SET_PAYMENT_METHOD(state, newValue) {
    if (!Object.values(PAYMENT_METHODS).includes(newValue)) {
      throw new Error('Invalid payment method!');
    }
    state.paymentMethod = newValue;
  },
  SET_CAMPAIGN_ID(state, newValue) {
    state.campaignId = newValue;
  },
  SET_CURRENCY(state, newValue) {
    const isSupportedCurrency = currencies.value.some((currency) => currency.value === newValue);
    state.currency = isSupportedCurrency ? newValue : DEFAULT_CURRENCY;
  },
  SET_PROGRAMMATIC_CURRENCY(state, newValue) {
    state.programmaticCurrency = newValue;
  },
  SET_CARD_EXPIRY(state, newValue) {
    state.cardExpiry = newValue;
  },
  SET_ORG_DEFAULT_CURRENCY(state, newValue) {
    state.orgDefaultCurrency = newValue;
  },
  SET_ENTITY_ID(state, newValue) {
    state.entityId = newValue;
  },
  SET_FREQUENCY(state, newValue) {
    state.upsell = null;
    state.frequency = newValue;
  },
  SET_UPSELL(state, newValue) {
    state.upsell = newValue;
  },
  RESET_UPSELL(state) {
    state.upsell = null;
  },

  SET_START_DATE(state, newValue) {
    state.startDate = newValue;
  },
  SET_NUM_CHARGES(state, newValue) {
    state.numCharges = newValue;
  },
  SET_PAY_COVER_FEES(state, newValue) {
    state.payCoverFees = newValue;
  },
  SET_CUSTOM_QUESTIONS(state, newValue) {
    state.customQuestions = newValue;
  },
  SET_COMMENT(state, newValue) {
    state.comment = newValue;
  },
  SET_FUNDRAISER_ID(state, newValue) {
    state.fundraiserId = newValue;
  },
  SET_SOLICITOR_ID(state, newValue) {
    state.solicitorId = newValue;
  },
  SET_SOLICITOR_SLUG(state, newValue) {
    state.solicitorSlug = newValue;
  },
  SET_IS_DONATING_ANONYMOUSLY(state, newValue) {
    state.anonymous = newValue;
  },
  SET_IS_DEDICATING(state, newValue) {
    state.isDedicating = newValue;
  },
  SET_DEDICATION(state, newValue) {
    state.dedication = newValue;
  },

  RESET_STATE(state) {
    const s = initialStateCopy;
    const fieldsToIgnore = ['gateway', 'CCGateway', 'frequency', 'entityId', 'prefill'];
    Object.keys(s).forEach((key) => {
      if (!fieldsToIgnore.includes(key)) {
        state[key] = s[key];
      }
    });

    if (Object.keys(state.prefill).length) {
      state.donorDetails = { ...state.prefill };
      state.meta = { prefill: { ...state.prefill } };
    }
  },
  SET_DONOR_DETAILS(state, newValue) {
    getSentryScope().setUser(newValue);
    state.donorDetails = newValue;
  },
  SET_PREFILL(state, newValue) {
    const prefill = PREFILL_FIELDS.reduce((fields, field) => {
      const value = typeof newValue?.[field] === 'string' ? newValue[field].trim() : '';
      if (value) {
        fields[field] = value;
      }
      return fields;
    }, {});
    const donorDetails = { ...state.donorDetails };

    Object.entries(state.prefill).forEach(([field, value]) => {
      if (donorDetails[field] === value) {
        delete donorDetails[field];
      }
    });

    state.prefill = prefill;
    state.donorDetails = { ...donorDetails, ...prefill };

    if (Object.keys(prefill).length) {
      state.meta = { ...state.meta, prefill: { ...prefill } };
    } else {
      const meta = { ...state.meta };
      delete meta.prefill;
      state.meta = meta;
    }

    getSentryScope().setUser(state.donorDetails);
  },
  SET_BILLING_ADDRESS(state, newValue) {
    state.billingAddress = newValue;
  },
  SET_DONOR_DETAILS_EMAIL(state, newValue) {
    state.donorDetails.email = sanitizeEmail(newValue);
  },
  SET_DONOR_DETAILS_PHONE(state, newValue) {
    state.donorDetails.phone = newValue;
  },
  SET_STRIPE_INTENT(state, newValue) {
    state.stripeIntent = newValue;
  },
  SET_CHARIOT_INTENT(state, newValue) {
    state.chariotIntent = newValue;
  },
  SET_CARDKNOX_TOKENS(state, newValue) {
    state.cardknoxTokens = newValue;
  },
  SET_DONATION_ID(state, newValue) {
    state.donationId = newValue;
  },
  SET_PLAID_LINK_TOKEN(state, newValue) {
    state.plaidLinkToken = newValue;
  },
  SET_PLAID_DATA(state, newValue) {
    state.plaidData = newValue;
  },
  SET_CAPTCHA_TOKEN(state, newValue) {
    state.hcaptchaToken = newValue;
  },
  SET_DRAFT_ID(state, newValue) {
    state.donationDraftId = newValue;
  },
  SET_UTM_PARAMETERS(state, newValue) {
    state.utmParameters = newValue;
  },
  SET_SOURCE_URL(state, newValue) {
    state.sourceUrl = newValue;
  }
};

export const getters = {
  ...zelle.getters,
  persistentDonationData(state) {
    return pick(state, [
      'amount',
      'frequency',
      'startDate',
      'numCharges',
      'payCoverFees',
      'anonymous',
      'isDedicating',
      'dedication',
      'donorDetails.email',
      'donorDetails.firstName',
      'donorDetails.lastName',
      'donorDetails.companyName'
    ]);
  },
  totalDonation(state, getters) {
    if (!getters.totalAmount) {
      return 0;
    }

    if (state.payCoverFees) {
      return getters.totalAmount + getters.totalFees;
    }
    return getters.totalAmount;
  },
  amount(state) {
    return state.amount;
  },
  amountPlusFees(state, getters) {
    const total = state.amount + (state.payCoverFees ? getters.chargeFees : 0);
    return Number(total.toFixed(2)) || 0;
  },
  totalAmount(state, getters) {
    if (state.frequency === 'single' || !getters.hasEndDate) {
      return state.amount;
    }

    const totalAmount = state.amount * state.numCharges;
    if (totalAmount < 0) {
      return 0;
    }
    return totalAmount;
  },
  chargeFees(state, _, rootState, rootGetters) {
    if (state.paymentMethod === PAYMENT_METHODS.ZELLE) return 0;
    const amount = state.amount;
    // We now have a single fee option, currently we still use cc
    const { fixedAmount: fixedFees, percentageAmount: percentageFees } =
      get(rootState.publicOrgSettings, 'settings.fees') || get(rootState.publicOrgSettings, 'settings.fees') || DEFAULT_FEES;

    // https://stackoverflow.com/a/37077952/10679649 (see also: https://stackoverflow.com/a/42984392/10679649)
    const totalIncludingFees = Number((amount + fixedFees.cc) / (1 - percentageFees.cc / 100));

    // Chariot supports only round numbers, so we round up the fees and not the amount
    if (state.paymentMethod === GATEWAYS.CHARIOT || rootGetters['campaign/isOnlyDafEnabled']) {
      return Math.ceil(totalIncludingFees) - amount;
    }

    return totalIncludingFees - amount;
  },
  totalFees(state, getters) {
    const chargeFees = getters.chargeFees;
    if (state.frequency === 'single' || !getters.hasEndDate) {
      return chargeFees;
    }
    return chargeFees * state.numCharges;
  },
  isCCDonation(state) {
    return [PAYMENT_METHODS.CARD, PAYMENT_METHODS.GPAY, PAYMENT_METHODS.APPLE_PAY].includes(state.paymentMethod);
  },
  isCharityCard(state) {
    return state.CCGateway?.provider === GATEWAYS.CARDKNOX && state.paymentMethod === PAYMENT_METHODS.DAF;
  },

  currencySymbol({ currency }) {
    return getCurrencySymbol(currency);
  },
  isRecurring(state) {
    return state.frequency !== 'single';
  },
  isOneTime(state) {
    return state.frequency === 'single';
  },
  donorFirstName(state) {
    return state.donorDetails.firstName;
  },
  hasEndDate(state, getters) {
    if (!getters?.isRecurring) {
      return false;
    }
    return getters.isRecurring && state.numCharges > 1;
  },
  isStartDateToday(state) {
    if (!state.startDate) {
      return true;
    }
    return dayjs(state.startDate).isSame(dayjs(), 'day');
  },
  isUsingElementFields(state) {
    if (state.CCGateway.provider === GATEWAYS.STRIPE) {
      return state.paymentMethod === PAYMENT_METHODS.CARD;
    }

    if (state.CCGateway.provider === GATEWAYS.CARDKNOX) {
      return state.paymentMethod === PAYMENT_METHODS.CARD || state.paymentMethod === PAYMENT_METHODS.ACH;
    }
    return false;
  },
  isPayPalAvailable: (_state, _getters, rootState) => {
    if (_state.frequency !== 'single') {
      return false;
    }
    return !!rootState.publicOrgSettings.gateways?.find((gateway) => gateway.provider === GATEWAYS.PAYPAL)?.integrationId;
  },
  getPrimaryCcGateway: (_state, _getters, rootState) => {
    const ccGateways = rootState.publicOrgSettings.gateways?.filter((gateway) => gateway.isCcGateway && gateway.isPrimary) || [];
    const primaryEntity =
      ccGateways.find((g) => g.isOverridden) ||
      ccGateways.find((g) => g.default) ||
      ccGateways[0] ||
      rootState.publicOrgSettings.gateways?.find((gateway) => gateway.provider === GATEWAYS.ZELLE);
    if (!primaryEntity) {
      throw new Error('No primary gateway found!');
    }

    return primaryEntity;
  },
  getGatewayByCurrency: (_state, _getters, rootState) => (currency) => {
    const entityForCurrency = rootState.publicOrgSettings.gateways?.find((gateway) => gateway.currency === currency);
    if (entityForCurrency) {
      return entityForCurrency;
    }

    return _getters.getPrimaryCcGateway;
  },
  // todo: gateway-refactor
  getCcGatewayByCurrency: (_state, _getters, rootState) => (currency) => {
    const ccGatewaysForCurrency = rootState.publicOrgSettings.gateways?.filter((gateway) => gateway.isCcGateway && gateway.currency === currency) || [];
    const ccGatewayByCurrency = ccGatewaysForCurrency.find((g) => g.isOverridden) || ccGatewaysForCurrency.find((g) => g.default) || ccGatewaysForCurrency[0];
    if (ccGatewayByCurrency) {
      return ccGatewayByCurrency;
    }

    return _getters.getPrimaryCcGateway;
  },
  primaryPayPalGateway: (_state, _getters, rootState) => {
    return rootState.publicOrgSettings.gateways?.find((gateway) => gateway.provider === GATEWAYS.PAYPAL && gateway.isPrimary);
  },
  getPayPalGatewayByCurrency: (_state, _getters, rootState) => (currency) => {
    const paypalGatewayByCurrency = rootState.publicOrgSettings.gateways?.find(
      (gateway) => gateway.currency === currency && gateway.provider === GATEWAYS.PAYPAL
    );
    if (paypalGatewayByCurrency) {
      return paypalGatewayByCurrency;
    }
    return _getters.primaryPayPalGateway;
  },
  entityId: (_state) => _state.entityId
};

export const actions = {
  ...zelle.actions,
  SET_FREQUENCY_ACTIONS({ commit }, newValue) {
    const isValidValue = ['single', 'daily', 'weekly', 'monthly', 'yearly'].includes(newValue);
    if (!isValidValue) {
      return;
    }

    // Only reset amount if frequency is changed
    if (state.frequency && state.frequency !== newValue && !state.donationItems.length) {
      commit('RESET_AMOUNT');
    }

    commit('SET_FREQUENCY', newValue);
    if (newValue === 'single') {
      commit('SET_START_DATE', null);
      commit('SET_NUM_CHARGES', null);
    } else {
      commit('SET_START_DATE', dayjs().toISOString());
      const KEREN_HASHNAH_CAMPAIGN_ID = 'de3902f0-8361-11f0-ab44-65b5075014bb';
      if (state.campaignId === KEREN_HASHNAH_CAMPAIGN_ID) {
        commit('SET_NUM_CHARGES', 12);
      }
    }
  },
  SET_CURRENCY_ACTIONS({ commit, getters }, newValue) {
    const isSupportedCurrency = currencies.value.some((currency) => currency.value === newValue);
    state.currency = isSupportedCurrency ? newValue : DEFAULT_CURRENCY;
    commit('SET_CURRENCY', isSupportedCurrency ? newValue : DEFAULT_CURRENCY);

    const gateway = getters.getCcGatewayByCurrency(newValue);
    commit('SET_CC_GATEWAY', gateway);
    const paypalGateway = getters.getPayPalGatewayByCurrency(newValue);
    commit('SET_PAYPAL_GATEWAY', paypalGateway);
    commit('SET_GATEWAY', gateway.provider);
    commit('SET_ENTITY_ID', gateway.entityId);
  },
  /** gets the intentId & donationId to be used for confirming payment details */
  async createDonation({ state, rootState, getters, commit }) {
    const origDonation = cloneDeep(state);
    const fees = {
      chargeFees: getters.chargeFees,
      feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
    };
    const donation = completeDonationDetails(origDonation, fees);
    const { data, error } = await request.post(`/public/donation/${rootState.orgId}/`, { donation });
    if (!error) {
      const { plaidLinkToken, donationId, client_secret: clientSecret } = data;

      switch (true) {
        case [PAYMENT_METHODS.CARD, PAYMENT_METHODS.APPLE_PAY, PAYMENT_METHODS.GPAY].includes(state.paymentMethod):
          commit('SET_STRIPE_INTENT', { id: getStripeIntentIdFromClientSecret(clientSecret), clientSecret });
          commit('SET_DONATION_ID', donationId);
          break;
        case state.paymentMethod === PAYMENT_METHODS.ACH:
          if (clientSecret) {
            commit('SET_STRIPE_INTENT', { id: getStripeIntentIdFromClientSecret(clientSecret), clientSecret });
          } else {
            // Temporary fallback for cached widgets while the server/widget rollout overlaps.
            commit('SET_PLAID_LINK_TOKEN', plaidLinkToken);
          }
          commit('SET_DONATION_ID', donationId);
          break;
      }
    } else {
      throw new Error(data);
    }
  },

  async donateWithCardknox({ rootState, getters, commit }) {
    const cardknox = await CardknoxService.getCardknoxObject();

    const getTokens = () => {
      return new Promise((resolve, reject) => {
        cardknox.getTokens(() => {
          try {
            const widgetIframe = document.querySelector('iframe[name="double-checkout"]');
            const cardToken = widgetIframe.contentDocument.querySelector("[data-ifields-id='card-number-token']").value;
            const cvvToken = widgetIframe.contentDocument.querySelector("[data-ifields-id='cvv-token']")?.value; // CVV is optional for some daf cards

            resolve({ cardToken, cvvToken });
          } catch (error) {
            reject(error);
          }
        });
      });
    };

    try {
      const { cardToken, cvvToken } = await getTokens();
      commit('SET_CARDKNOX_TOKENS', { cardToken, cvvToken });

      const fees = {
        chargeFees: getters.chargeFees,
        feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
      };
      const origDonation = cloneDeep(state);
      const donation = completeDonationDetails(origDonation, fees);

      const { data, error } = await request.post(`/public/donation/${rootState.orgId}/`, {
        donation,
        gateway: GATEWAYS.CARDKNOX
      });

      if (!error) {
        return data;
      }

      throw new Error(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async donateWithCardknoxACH({ rootState, getters, commit }) {
    const cardknox = await CardknoxService.getCardknoxObject();

    const getTokens = () => {
      return new Promise((resolve, reject) => {
        cardknox.getTokens(() => {
          try {
            const widgetIframe = document.querySelector('iframe[name="double-checkout"]');
            const achToken = widgetIframe.contentDocument.querySelector("[data-ifields-id='ach-token']").value;
            resolve({ achToken });
          } catch (error) {
            reject(error);
          }
        });
      });
    };

    try {
      const { achToken } = await getTokens();
      commit('SET_CARDKNOX_TOKENS', { achToken });

      const fees = {
        chargeFees: getters.chargeFees,
        feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
      };
      const origDonation = cloneDeep(state);
      const donation = completeDonationDetails(origDonation, fees);

      const { data, error } = await request.post(`/public/donation/${rootState.orgId}/`, {
        donation,
        gateway: GATEWAYS.CARDKNOX
      });

      if (!error) {
        return data;
      }

      throw new Error(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async paypalIntent({ getters }, { orgId, donation }) {
    try {
      // todo: send entire donation
      const { data: id, error } = await request.post(`public/donation/${orgId}/paypal`, {
        amount: getters.amountPlusFees,
        currency: donation.currency,
        gatewayIntegrationId: donation.paypalGateway.integrationId,
        payCoverFees: donation.payCoverFees,
        campaignId: donation.campaignId
      });
      if (!error) {
        return id;
      }
      throw new Error(error.message);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async donateWithPayPal({ state, getters, rootState, commit }, orderID) {
    const fees = {
      chargeFees: getters.chargeFees,
      feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
    };

    const origDonation = cloneDeep(state);
    const donation = completeDonationDetails(origDonation, fees);

    const { data, error } = await request.post(`/public/donation/${rootState.orgId}/paypal/approve/?orderID=${orderID}`, { donation });
    const { donationId, token } = data;
    commit('SET_DONATION_ID', donationId);
    if (!error) {
      return token;
    } else {
      throw new Error(data);
    }
  },

  async donateWithGrow({ state, getters, rootState, commit }) {
    const fees = {
      chargeFees: getters.chargeFees,
      feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
    };

    const origDonation = cloneDeep(state);
    const donation = completeDonationDetails(origDonation, fees);

    const { data, error } = await request.post(`/public/donation/${rootState.orgId}/`, { donation });
    const { donationId, token } = data;
    commit('SET_DONATION_ID', donationId);
    if (!error) {
      return token;
    } else {
      throw new Error(data);
    }
  },

  async donateWithChariot({ getters, rootState }) {
    const fees = {
      chargeFees: getters.chargeFees,
      feesList: get(rootState.publicOrgSettings, 'settings.fees') || []
    };
    const origDonation = cloneDeep(state);
    const donation = completeDonationDetails(origDonation, fees);

    const { data, error } = await request.post(`/public/donation/${rootState.orgId}/`, { donation });

    if (!error) {
      return data;
    } else {
      throw new Error(data);
    }
  },

  async confirmPaymentStripeElements({ getters, commit }, stripeElement) {
    return confirmPaymentStripe({ getters, commit }, stripeElement);
  },

  async confirmStripePaymentRequest({ getters, commit }, paymentRequestId) {
    return confirmPaymentStripe({ getters, commit }, null, paymentRequestId);
  },

  async confirmStripeAch({ commit }) {
    return confirmFinancialConnectionsAch(commit);
  },

  async confirmDonation({ state, getters, rootState }) {
    const donation = state;
    const isFinancialConnectionsAch = state.paymentMethod === PAYMENT_METHODS.ACH && Boolean(donation.stripeIntent?.id);
    const isAchIntentConfirmed = ['processing', 'succeeded'].includes(donation.stripeIntent?.status);
    const isPaymentConfirmed = getters.isCCDonation
      ? donation.stripeIntent?.status === 'succeeded'
      : isFinancialConnectionsAch
      ? isAchIntentConfirmed
      : Boolean(donation.plaidData);
    if (!donation.donationId || !isPaymentConfirmed) {
      throw new Error('Calling confirmDonation without confirmed payment details and donationId');
    }

    const { data, error } = await request.post(`/public/donation/${rootState.orgId}/${state.donationId}/confirm`, {
      gateway: GATEWAYS.STRIPE,
      paymentMethod: state.paymentMethod,
      data: getters.isCCDonation || isFinancialConnectionsAch ? donation.stripeIntent : donation.plaidData
    });

    if (error) {
      throw new Error(data);
    }
    return data;
  }
};

const initialStateCopy = JSON.parse(JSON.stringify(state));

function completeDonationDetails(donation, fees) {
  const chargeFees = donation.payCoverFees ? fees.chargeFees : 0;
  const now = dayjs().toISOString();

  // Fees
  donation.coverFeesAmount = Number(chargeFees.toFixed(2));
  donation.feesList = fees.feesList;

  if (donation.frequency === 'single') {
    // reset dates in case it was set
    donation.startDate = null;
    donation.numCharges = null;
  } else if (donation.frequency !== 'single') {
    donation.amount = Number(donation.amount.toFixed(2));
    donation.startDate = donation.startDate || now;
  }
  return donation;
}

async function confirmPaymentStripe({ commit }, card, paymentRequestId = null) {
  const donation = state;
  let stripe;
  if (paymentRequestId) {
    // Using payment request outside iframe
    stripe = await StripeService.getStripeObject(true);
  } else {
    // for elements
    stripe = await StripeService.getStripeObject();
  }

  const confirm = donation.frequency !== 'single' ? stripe.confirmCardSetup : stripe.confirmCardPayment;
  // will get either paymentIntent or setupIntent (or error)
  const { paymentIntent, setupIntent, error } = await confirm(donation.stripeIntent.clientSecret, {
    payment_method: paymentRequestId || {
      card,
      billing_details: {
        address: {
          postal_code: get(donation.donorDetails, 'zip') // TODO: more?
        }
      }
    }
  });
  getSentryScope().addBreadcrumb({
    category: 'stripe',
    message: 'confirmPaymentStripe response',
    data: { paymentIntent, setupIntent, error }
  });
  const clientSecret = donation.stripeIntent?.clientSecret || donation.stripeIntent?.client_secret;
  let intent = normalizeStripeIntent(paymentIntent || setupIntent || getIntentFromStripeError(error), clientSecret);

  if (error) {
    if (!isSucceededStripeIntent(intent) && shouldResolveStripeIntent(error)) {
      intent = await retrieveStripeIntent(stripe, donation).catch((retrieveError) => {
        getSentryScope().addBreadcrumb({
          category: 'stripe',
          message: 'retrieveStripeIntent after confirm error failed',
          data: { error: retrieveError }
        });
        return intent;
      });
    }

    if (isSucceededStripeIntent(intent)) {
      commit('SET_STRIPE_INTENT', intent);
      return;
    }

    throw new Error(error.message);
  }

  if (intent) {
    commit('SET_STRIPE_INTENT', intent);
  }
}

async function confirmFinancialConnectionsAch(commit) {
  const donation = state;
  const stripe = await StripeService.getStripeObject();
  const clientSecret = donation.stripeIntent?.clientSecret || donation.stripeIntent?.client_secret;
  const isRecurring = donation.frequency !== 'single';
  const billingDetails = {
    name: [donation.donorDetails?.firstName, donation.donorDetails?.lastName].filter(Boolean).join(' '),
    email: donation.donorDetails?.email
  };
  const collectionParams = {
    clientSecret,
    params: {
      payment_method_type: 'us_bank_account',
      payment_method_data: {
        billing_details: billingDetails
      }
    },
    expand: ['payment_method']
  };
  const collected = isRecurring ? await stripe.collectBankAccountForSetup(collectionParams) : await stripe.collectBankAccountForPayment(collectionParams);

  getSentryScope().addBreadcrumb({
    category: 'stripe',
    message: 'collectBankAccount response',
    data: {
      intentId: collected.paymentIntent?.id || collected.setupIntent?.id,
      status: collected.paymentIntent?.status || collected.setupIntent?.status,
      errorCode: collected.error?.code
    }
  });

  if (collected.error) {
    throw new Error(collected.error.message);
  }

  let intent = collected.paymentIntent || collected.setupIntent;
  if (intent?.status === 'requires_payment_method') {
    throw new Error('Bank account connection was canceled.');
  }
  if (intent?.status !== 'requires_confirmation') {
    throw new Error(`Unexpected bank account status: ${intent?.status || 'unknown'}`);
  }

  const confirmed = isRecurring ? await stripe.confirmUsBankAccountSetup(clientSecret) : await stripe.confirmUsBankAccountPayment(clientSecret);
  intent = confirmed.paymentIntent || confirmed.setupIntent || getIntentFromStripeError(confirmed.error);

  getSentryScope().addBreadcrumb({
    category: 'stripe',
    message: 'confirmUsBankAccount response',
    data: {
      intentId: confirmed.paymentIntent?.id || confirmed.setupIntent?.id,
      status: confirmed.paymentIntent?.status || confirmed.setupIntent?.status,
      errorCode: confirmed.error?.code
    }
  });

  if (confirmed.error) {
    throw new Error(confirmed.error.message);
  }

  const allowedStatuses = isRecurring ? ['succeeded'] : ['processing', 'succeeded'];
  if (!allowedStatuses.includes(intent?.status)) {
    throw new Error(`Bank account confirmation is incomplete: ${intent?.status || 'unknown'}`);
  }

  commit('SET_STRIPE_INTENT', normalizeStripeIntent(intent, clientSecret));
}

function getIntentFromStripeError(error) {
  return error?.payment_intent || error?.setup_intent || null;
}

function isSucceededStripeIntent(intent) {
  return intent?.status === 'succeeded';
}

function shouldResolveStripeIntent(error) {
  return (
    ['api_connection_error', 'api_error'].includes(error?.type) || ['payment_intent_unexpected_state', 'setup_intent_unexpected_state'].includes(error?.code)
  );
}

async function retrieveStripeIntent(stripe, donation) {
  const clientSecret = donation.stripeIntent?.clientSecret || donation.stripeIntent?.client_secret;
  if (!clientSecret) {
    return null;
  }

  const response = donation.frequency !== 'single' ? await stripe.retrieveSetupIntent(clientSecret) : await stripe.retrievePaymentIntent(clientSecret);
  getSentryScope().addBreadcrumb({
    category: 'stripe',
    message: 'retrieveStripeIntent after confirm error response',
    data: response
  });

  return normalizeStripeIntent(response.paymentIntent || response.setupIntent || getIntentFromStripeError(response.error), clientSecret);
}

function normalizeStripeIntent(intent, clientSecret) {
  if (!intent && !clientSecret) {
    return null;
  }

  return {
    ...(intent || {}),
    id: intent?.id || getStripeIntentIdFromClientSecret(clientSecret),
    clientSecret: intent?.clientSecret || intent?.client_secret || clientSecret
  };
}

function getStripeIntentIdFromClientSecret(clientSecret) {
  return clientSecret?.split('_secret_')[0] || null;
}
